/**
 * §6.1 權限清單 —— 程式碼定義的固定清單，前後端共用。
 *
 * 權限是程式碼，角色是資料。畫面上只能組合權限成角色，不能新增權限。
 */
/** §6.1 作業 */
const OPS = [
    { code: 'receiving.register', label: '進庫逐片建檔', description: '為交接進來的太陽能板逐片建立 PV 號與基本資料。', category: 'ops' },
    { code: 'receiving.post', label: '進庫過帳', description: '把已建檔的板子過帳入庫，產生在庫料件。', category: 'ops' },
    { code: 'workorder.open', label: '開工單', description: '在某台設備上開立工單。同一台設備同時只能有一張（§10.1）。', category: 'ops' },
    { code: 'workorder.close', label: '關工單', description: '結束工單。無法結算的工單關閉前會顯示原因。', category: 'ops' },
    { code: 'issue.post', label: '領料過帳', description: '把在庫料件領到工單上。', category: 'ops' },
    { code: 'issue.approve', label: '領料個案核可', description: '核可按重量挑料或多型號混批的領料（解析度降為 R3，§9 規則 9）。', category: 'ops' },
    { code: 'receipt.post', label: '繳庫過帳', description: '把工單的產出繳回庫，含逐片或整批量測。', category: 'ops' },
    { code: 'receipt.supplement', label: '補秤', description: '為先前沒有重量的繳庫列補上量測值。', category: 'ops' },
    { code: 'shipment.post', label: '出廠過帳', description: '把成品料件出廠出庫。', category: 'ops' },
    { code: 'stockcount.record', label: '記錄盤點', description: '記錄實地盤點結果，差異以調整事件處理。', category: 'ops' },
    { code: 'adjustment.post', label: '調整（沖正）過帳', description: '以沖正事件修正帳務。事件流只能新增，這是唯一的修正方式（§9 規則 4）。', category: 'ops' },
    { code: 'weigh.verify', label: '量測覆核', description: '雙人覆核的第二人，覆核他人輸入的量測值。', category: 'ops' },
    { code: 'panel.scrap', label: '單片報廢', description: '把單一片板子標記為報廢。', category: 'ops' },
    { code: 'panel.detach', label: '單片抽離', description: '把單一片板子從料件中抽離。', category: 'ops' },
];
/** §6.1 查詢 */
const QUERY = [
    { code: 'inventory.read', label: '庫存總覽', description: '查看在庫料件、儲位與數量。', category: 'query' },
    { code: 'panel.read', label: '單片追溯', description: '以 PV 號查詢單一片板子的完整履歷與收支帳。', category: 'query' },
    { code: 'balance.read', label: '站台結算', description: '查看工單的站台結算與投入產出。', category: 'query' },
    { code: 'audit.read', label: '稽核軌跡', description: '查看領域事件流（業務資料的完整變更紀錄）。', category: 'query' },
    { code: 'report.read', label: '全鏈平衡與報表', description: '查看回收率、全鏈平衡等報表，並可下載（§11.5）。', category: 'query' },
    { code: 'report.crosssite', label: '跨廠區彙總報表', description: '查看跨廠區的彙總報表。必須顯式指定廠區清單（§12.4）。', category: 'query' },
];
/** §6.1 作業設定 */
const SETTINGS = [
    { code: 'settings.read', label: '檢視設定', description: '唯讀檢視作業設定的所有頁籤。', category: 'settings' },
    { code: 'settings.dictionary.write', label: '字典與基準', description: '料號字典、型號基準、容器、儲位、秤具、批量規則。', category: 'settings' },
    { code: 'settings.station.write', label: '站台與設備實例', description: '新增與編輯站台、以及站台底下的設備實例。', category: 'settings' },
    {
        code: 'settings.measurement.write',
        label: '量測點的啟用與粒度',
        description: '改動量測點會改變後續所有工單的資料品質，且不影響已完成的工單（§9 規則 14）。全系統風險最高的權限，預設只給主管。',
        category: 'settings',
        highRisk: true,
    },
    { code: 'settings.device.write', label: '設備（秤具）管理', description: '註冊設備、停用、輪替金鑰（§13.9 #3）。', category: 'settings' },
    {
        code: 'settings.model.create',
        label: '新增型號基準',
        description: '新增一個太陽能板型號，以及編輯「還沒有任何板子或工單用過」的型號。' +
            '現場遇到沒見過的型號時要當場建得起來，所以預設所有角色都有這個權限。' +
            '一旦有板子掛上去，再編輯就需要「字典與基準」權限（§9 規則 14：基準會影響分攤數字）。',
        category: 'settings',
    },
    { code: 'idpool.generate', label: '產生號碼', description: '為廠區產生新的 PV 號或料件號。', category: 'settings' },
    { code: 'idpool.reserve', label: '預提號碼', description: '預先取出一段號碼交給現場使用。', category: 'settings' },
    { code: 'idpool.reclaim', label: '回收號碼', description: '把未使用的號碼收回號碼池。', category: 'settings' },
];
/** §6.1 系統 */
const SYSTEM = [
    { code: 'account.read', label: '檢視帳號', description: '查看帳號清單與其角色。', category: 'system' },
    { code: 'account.write', label: '帳號維護', description: '建立、停用、重設密碼、指派角色。帳號只停用不刪除（§5.1）。', category: 'system' },
    { code: 'role.write', label: '角色與權限', description: '建立與編輯自訂角色。內建角色不可編輯（§6.3）。', category: 'system' },
    { code: 'site.read', label: '檢視廠區', description: '查看廠區清單與靜態資料。', category: 'system' },
    {
        code: 'site.write',
        label: '廠區維護',
        description: '建立、編輯、啟用／暫停廠區（§12.3）。只授予集團層級管理者，且只能以「全廠區」授予。',
        category: 'system',
        highRisk: true,
    },
    { code: 'session.manage', label: '線上工作階段', description: '檢視誰在線上並強制登出。', category: 'system' },
    { code: 'system.smtp', label: 'SMTP 設定與測試', description: '設定寄信主機並寄送測試信（§5.4）。', category: 'system' },
    { code: 'system.auth', label: '登入方式開關', description: '開關密碼登入與 Email 驗證碼登入（§5.2）。', category: 'system' },
    { code: 'system.backup', label: '備份與系統維護', description: '系統維護畫面與手動觸發備份（§11.7）。', category: 'system' },
    { code: 'authaudit.read', label: '認證稽核紀錄', description: '查看登入、帳號、角色、廠區的管理動作紀錄。', category: 'system' },
];
export const PERMISSIONS = Object.freeze([...OPS, ...QUERY, ...SETTINGS, ...SYSTEM]);
export const PERMISSION_CODES = Object.freeze(PERMISSIONS.map((p) => p.code));
const CODE_SET = new Set(PERMISSION_CODES);
export function isPermission(code) {
    return CODE_SET.has(code);
}
export function permissionDef(code) {
    return PERMISSIONS.find((p) => p.code === code);
}
export const CATEGORY_LABELS = {
    ops: '作業',
    query: '查詢',
    settings: '作業設定',
    system: '系統',
};
/** 展開 'account.*' 這種簡寫。只在下面的內建角色定義裡使用。 */
function expand(...patterns) {
    const out = new Set();
    for (const pattern of patterns) {
        if (pattern.endsWith('.*')) {
            const prefix = pattern.slice(0, -1); // 'account.'
            const matched = PERMISSION_CODES.filter((c) => c.startsWith(prefix));
            if (matched.length === 0)
                throw new Error(`權限簡寫沒有對應任何權限：${pattern}`);
            matched.forEach((c) => out.add(c));
        }
        else {
            if (!CODE_SET.has(pattern))
                throw new Error(`未知的權限代碼：${pattern}`);
            out.add(pattern);
        }
    }
    return [...out];
}
const ALL_OPS = OPS.map((p) => p.code);
/**
 * 新增型號：**所有內建角色都有**。
 *
 * 現場遇到沒見過的型號時，如果要等主管才建得起來，操作員就只能先選「未辨識」，
 * 而未辨識的板子分攤用的是全型號平均值——那個數字之後很難修回來。
 * 讓任何人都能當場把型號建起來，比事後補正便宜得多。
 *
 * 「編輯已被使用的型號」是另一回事，那需要 settings.dictionary.write（見 §6.1 的說明）。
 */
const MODEL_CREATE = 'settings.model.create';
const ALL_QUERY_SINGLE_SITE = QUERY.filter((p) => p.code !== 'report.crosssite').map((p) => p.code);
/** §6.2 內建角色。is_builtin = true，畫面上不可編輯或刪除。 */
export const BUILTIN_ROLES = Object.freeze([
    {
        code: 'admin',
        name: '系統管理者',
        description: '管帳號、角色、系統設定。預設不含任何作業權限（§6.2 職責分離）。',
        permissions: expand('account.*', 'role.write', 'session.manage', 'system.smtp', 'system.auth', 'system.backup', 'site.read', 'authaudit.read', 'settings.read', 'audit.read', MODEL_CREATE),
    },
    {
        code: 'group_admin',
        name: '集團管理者',
        description: '系統管理者的全部權限，外加建立與編輯廠區。只能以「全廠區」授予。',
        permissions: expand('account.*', 'role.write', 'session.manage', 'system.smtp', 'system.auth', 'system.backup', 'site.read', 'site.write', 'authaudit.read', 'settings.read', 'audit.read', MODEL_CREATE),
        allSitesOnly: true,
    },
    {
        code: 'supervisor',
        name: '廠長／主管',
        description: '全部作業權限、作業設定、號碼池與查詢。',
        permissions: expand(...ALL_OPS, 'settings.*', 'idpool.*', ...ALL_QUERY_SINGLE_SITE, 'site.read'),
    },
    {
        code: 'verifier',
        name: '覆核者',
        description: '雙人覆核的第二人，加上全部查詢。',
        permissions: expand('weigh.verify', ...ALL_QUERY_SINGLE_SITE, MODEL_CREATE),
    },
    {
        code: 'operator',
        name: '操作員',
        description: '現場作業。預設不含任何系統權限（§6.2 職責分離）。',
        permissions: expand('receiving.*', 'workorder.open', 'workorder.close', 'issue.post', 'receipt.post', 'receipt.supplement', 'stockcount.record', 'inventory.read', 'panel.read', 'balance.read', MODEL_CREATE),
    },
    {
        code: 'viewer',
        name: '檢視者',
        description: '只讀。',
        permissions: expand('inventory.read', 'panel.read', 'balance.read', 'report.read', MODEL_CREATE),
    },
]);
export const ALL_SITES = '*';
export function builtinRole(code) {
    return BUILTIN_ROLES.find((r) => r.code === code);
}
//# sourceMappingURL=permissions.js.map